/* LLCL / La Troupe / Bag V2
 * Presentation adapter for the existing Prestige CartDrawer instance.
 * No cart cache, add-to-cart request, checkout or analytics replacement.
 */
import { timeline } from 'vendor';
const mounted = new WeakSet();
function mount() {
  const panel = document.querySelector('cart-drawer[data-ll-bag-confirmation]');
  if (!panel || mounted.has(panel)) return;
  mounted.add(panel);
  const abort = new AbortController();
  const options = { signal: abort.signal };
  const state = { addition: null, waiting: false, frame: 0, timer: 0, headerStyle: null, closing: null };
  const nativeShow = panel.show.bind(panel);
  const nativeHide = panel.hide.bind(panel);
  const header = document.querySelector('x-header') || document.querySelector('.shopify-section--header');
  function revealHeader() {
    if (!header || state.headerStyle) return;
    state.headerStyle = ['transform', 'transition'].map(property => ({ property, value: header.style.getPropertyValue(property), priority: header.style.getPropertyPriority(property) }));
    // Only while this panel is open. Preserve native hide-on-scroll private state.
    header.style.setProperty('transition', 'none', 'important');
    header.style.setProperty('transform', 'translateY(0)', 'important');
  }
  function restoreHeader() {
    if (!header || !state.headerStyle) return;
    for (const entry of state.headerStyle) {
      if (entry.value) header.style.setProperty(entry.property, entry.value, entry.priority);
      else header.style.removeProperty(entry.property);
    }
    state.headerStyle = null;
  }
  const viewport = () => {
    const vv = window.visualViewport;
    const useViewport = vv && Math.abs(vv.scale - 1) < .01;
    const height = useViewport ? vv.height : window.innerHeight;
    const offset = useViewport ? vv.offsetTop : 0;
    const headerBottom = header?.getBoundingClientRect().bottom || 0;
    const top = Math.max(offset, Math.min(Math.max(0, headerBottom), offset + height - 160));
    panel.style.setProperty('--ll-bag-top', `${Math.round(top)}px`);
    panel.style.setProperty('--ll-bag-available', `${Math.max(160, Math.round(offset + height - top))}px`);
  };
  const scheduleViewport = () => {
    cancelAnimationFrame(state.frame);
    state.frame = requestAnimationFrame(viewport);
  };
  function present(addition) {
    // Editorial overview and add-confirmation are visual states of one
    // native Prestige dialog. Always retain the real Shopify cart order/amount.
    const rows = [...panel.querySelectorAll('[data-ll-bag-line]')];
    const selected = addition
      ? rows.find(row => addition.keys.has(row.dataset.key))
          || rows.find(row => addition.variants.has(row.dataset.variant))
      : null;
    const mode = selected ? 'added' : 'overview';
    panel.dataset.llBagMode = mode;
    const maxVisible = 4;
    const visibleRows = [...(selected ? [selected] : []), ...rows.filter(row => row !== selected)].slice(0, maxVisible);
    rows.forEach(row => {
      row.hidden = !visibleRows.includes(row);
      row.toggleAttribute('data-ll-bag-highlight', row === selected);
      row.style.order = row === selected ? '-1' : '';
    });
    panel.querySelectorAll('[data-ll-bag-added-title]').forEach(node => { node.hidden = mode !== 'added'; });
    panel.querySelectorAll('[data-ll-bag-overview-title]').forEach(node => { node.hidden = mode === 'added'; });
    const more = panel.querySelector('[data-ll-bag-more]');
    if (more) more.hidden = rows.length <= visibleRows.length;
    viewport();
  }
  if (window.themeVariables?.settings) window.themeVariables.settings.cartType = 'drawer';
  async function openPanel(animate = true, addition = null) {
    if (state.closing) {
      await state.closing;
      await new Promise(requestAnimationFrame);
    }
    present(addition);
    return nativeShow(animate);
  }
  panel.show = function (animate = true) {
    return state.waiting ? Promise.resolve() : openPanel(animate);
  };
  panel.hide = function () {
    if (state.closing) return state.closing;
    if (!panel.open) return nativeHide();
    const closing = nativeHide();
    state.closing = closing;
    closing.finally(() => { if (state.closing === closing) state.closing = null; });
    return closing;
  };
  function transition(enter) {
    if (enter) revealHeader();
    viewport();
    const duration = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : enter ? .26 : .18;
    const content = panel.getShadowPartByName('content');
    content.style.marginInlineStart = '0';
    return timeline([
      [panel.getShadowPartByName('overlay'), { opacity: enter ? [0, 1] : [1, 0] }, { duration }],
      [content, { transform: enter ? ['translateY(-100%)', 'translateY(0)'] : ['translateY(0)', 'translateY(-100%)'] }, { duration, at: '<', easing: [.22, 1, .36, 1] }]
    ]);
  }
  panel.createEnterAnimationControls = () => transition(true);
  panel.createLeaveAnimationControls = () => transition(false);
  document.addEventListener('variant:add', event => {
    const items = Array.isArray(event.detail?.items) ? event.detail.items : [];
    state.addition = { keys: new Set(items.map(item => String(item.key || ''))), variants: new Set(items.map(item => String(item.variant_id || item.id || ''))) };
  }, { ...options, capture: true });
  document.addEventListener('cart:change', event => {
    if (event.detail?.baseEvent !== 'variant:add') return;
    state.waiting = true;
    state.addition ||= { keys: new Set(), variants: new Set() };
    clearTimeout(state.timer);
    state.timer = setTimeout(() => { state.waiting = false; state.addition = null; }, 10000);
  }, { ...options, capture: true });
  panel.addEventListener('cart-drawer:refreshed', () => {
    clearTimeout(state.timer);
    const addition = state.waiting ? state.addition : null;
    state.waiting = false;
    present(addition);
    state.addition = null;
    if (addition && panel.querySelector('[data-ll-bag-line]')) openPanel(true, addition);
  }, options);
  panel.addEventListener('dialog:after-show', () => { panel.querySelector('[data-ll-bag-title]')?.focus({ preventScroll: true }); }, options);
  panel.addEventListener('dialog:after-hide', () => { restoreHeader(); present(null); }, options);
  window.addEventListener('resize', scheduleViewport, { ...options, passive: true });
  window.addEventListener('scroll', () => { if (panel.open) scheduleViewport(); }, { ...options, passive: true });
  window.visualViewport?.addEventListener('resize', scheduleViewport, { ...options, passive: true });
  const observer = new ResizeObserver(scheduleViewport);
  if (header) observer.observe(header);
  panel.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => {
    observer.disconnect(); abort.abort(); clearTimeout(state.timer); cancelAnimationFrame(state.frame); restoreHeader();
  }, { once: true });
  present(null);
}
customElements.whenDefined('cart-drawer').then(mount);
document.addEventListener('shopify:section:load', () => customElements.whenDefined('cart-drawer').then(mount));
